import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { Button, Stack, Switch, Typography } from '@mui/material';
import { useFormik } from 'formik';

import debounce from 'lodash/debounce'; // Import debounce function
// Function to create formik handlers with debounce validation
export const formikFieldHandlers = (formik, delay = 300) => {
  // Debounced field validation function (except required)
  const debouncedValidateField = debounce((name) => {
    //formik.validateField(name); // Validate the field
    const fieldError = formik.errors[name];
    if (fieldError && fieldError !== 'Required') {
      formik.validateField(name); // Validate the field
    }
  }, delay);

  // Field change handler with debounced validation
  const handleFieldChange = (e) => {
    formik.handleChange(e);
    debouncedValidateField(e.target.name); // Validate with debounce
  };

  // Field blur handler with immediate validation
  const handleFieldBlur = (e) => {
    formik.handleBlur(e);
    // Do not trigger required validation on blur
    //formik.validateField(e.target.name); // Validate on blur
  };

  return {
    handleFieldChange,
    handleFieldBlur,
  };
};
/**
 * How to use
 *
 * import { formikFieldHandlers } from './formik-helpers'; // Import the common handler
 *
 * // Get field handlers from the common helper file
 * const { handleFieldChange, handleFieldBlur } = formikFieldHandlers(formik);
 *
 * // TextField tag add more attributes
 * error={!!(formik.errors.<field_name> && formik.touched.<field_name>)}
 * helperText={formik.errors.<field_name> && formik.touched.<field_name>}
 * onBlur={handleFieldBlur}  // Validate on blur
 * onChange={handleFieldChange}  // Validate on change with debounce
 *
 * Validation's fields must be declare in "initialValues: { <fields_name> }"
 */
export const useCustomFormik = ({
  initialValues = {},
  validationSchema,
  onSubmit,
}) => {
  initialValues = {
    ...initialValues,
    submit: null,
    isVerified: false
  };
  
  // Create the formik instance with dynamic initialValues and validationSchema
  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnChange: false, // Disable automatic change validation globally
    validateOnBlur: false, // Disable automatic blur validation globally
    onSubmit,
  });

  // Get debounced field change and blur handlers
  return formik;
};

/**
 * Using the custom formik hook
 *
 * import { useCustomFormik } from './formik-config'; // Import the common formik config
 *
 * const formik = useCustomFormik({
 *  initialValues: { ... },
 *  validationSchema,
 *  onSubmit: async (values, helpers) => { ... }
 * });
 *
 * Validation's fields must be declare in "initialValues: { <fields_name> }"
 */
const SubmitButton = ({
  formik,
  isRemove = false,
  labelSubmit = 'Cập nhật',
  labelRemove = 'Xóa dữ liệu',
  hasVerified = false,
  buttonColor = 'error.main',
  onVerifyChange,
  disabled = false,
}) => {
  const [localVerified, setLocalVerified] = useState(hasVerified);
  const [delayedRemove, setDelayedRemove] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDelayedRemove(isRemove);
    }, 250);
    return () => clearTimeout(timer);
  }, [isRemove]);

  useEffect(() => {
    setLocalVerified(hasVerified);
    formik.setFieldValue('isVerified', hasVerified);
  }, [hasVerified]);

  const buttonStyles = {
    backgroundColor: localVerified ? buttonColor : 'primary.main',
    '&:hover': {
      backgroundColor: localVerified ? 'darkred' : 'primary.dark',
    },
  };

  const handleSwitchChange = (event) => {
    const { checked } = event.target;
    formik.setFieldValue('isVerified', checked);
    if (onVerifyChange) 
      onVerifyChange(checked); // If parent handles it, defer to parent
    else
      setLocalVerified(checked); // Default internal behavior
  };

  return (
    <Stack
      direction={{
        xs: 'column',
        sm: 'row',
      }}
      alignItems="center"
    >
      <Button
        disabled={formik.isSubmitting || disabled}
        type="submit"
        variant="contained"
        sx={{
          ...buttonStyles,
          order: { xs: 0, sm: 1 },
        }}
      >
        {labelSubmit}
      </Button>

      {delayedRemove && (
        <Stack
          direction="row"
          alignItems="center"
          sx={{
            order: { xs: 1, sm: 0 },
            mt: { xs: 3, sm: 0 },
            mr: { xs: 0, sm: 3 },
          }}
        >
          <Typography color="text.primary" sx={{ mr: 0.5 }}>{labelRemove}</Typography>
          <Switch
            color="primary"
            edge="start"
            name="isVerified"
            checked={localVerified}
            onChange={handleSwitchChange}
          />
        </Stack>
      )}
    </Stack>
  );
};
export default SubmitButton;

SubmitButton.propTypes = {
  formik: PropTypes.object.isRequired,
  isRemove: PropTypes.bool,
  labelSubmit: PropTypes.string,
  labelRemove: PropTypes.string,
  hasVerified: PropTypes.bool,
  buttonColor: PropTypes.string,
  onVerifyChange: PropTypes.func,
  disabled: PropTypes.bool,
};
